import { c as _c } from "react/compiler-runtime";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { getRoleMenuItemSx, RoleMenuItem } from "@components/role/role-menu-item";
import { ChangeRoleEntityType, useChangeRoleBulkDialogContent } from "@components/table/members/bulk-actions/bulk-change-role-helper";
import { useBulkRoleXgSeats } from "@components/table/members/bulk-actions/use-bulk-role-xg-seats";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useXgSeatRequirement } from "@hooks/use-xg-seat-requirement";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { AssignableUpdateCompanyMemberRole } from "@custom-types/member-types";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateMany, updateOne } from "@store/table/table-slice";
import { currentUserSelector } from "@store/user/user-selector";
import { formatUserRoleType } from "@utils/data-display";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { doesWorkspaceRoleRequireXgSeat } from "@utils/xg-seat-utils";
import { useEffect, useState } from "react";
import { Text, UserRoleIcon, MenuItem } from "@flat-ui";
/**
 * Bulk-assignable workspace roles (Group Viewer is always excluded upstream in
 * `AssignableUpdateCompanyMemberRole`). Admin can only be assigned when the
 * workspace can assign managers. "No workspace role" is offered for teams, and
 * also for members when managers can't be assigned (Scan Essential and below),
 * where every member is a viewer that can be safely downgraded. When the plan
 * may require a Sphere XG seat for roles, teams are not offered the roles that
 * need one, since teams cannot hold a seat.
 */
function getBulkAssignableCompanyRoles(entityType, canAssignManagers, canPlanRequireXgSeat) {
  const canAssignNoRole = entityType === "teams" || !canAssignManagers;
  return AssignableUpdateCompanyMemberRole.filter(role => {
    if (entityType === "teams" && canPlanRequireXgSeat && doesWorkspaceRoleRequireXgSeat(role)) {
      return false;
    }
    if (role === "COMPANY_EXECUTIVE") {
      return canAssignManagers;
    }
    if (role === "MEMBER") {
      return canAssignNoRole;
    }
    return true;
  });
}

/** Button allowing to change role of members or teams in company */
export function BulkChangeCompanyRole(t0) {
  const $ = _c(58);
  const {
    companyId,
    entityType
  } = t0;
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [newRole, setNewRole] = useState("MEMBER");
  const currentUser = useAppSelector(currentUserSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  let t1;
  if ($[0] !== entityType) {
    t1 = selectedEntitiesSelector(entityType);
    $[0] = entityType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedEntities = useAppSelector(t1);
  const mode = useBulkActionsMode();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const canPlanRequireXgSeat = useXgSeatRequirement() !== "notRequired";
  const dispatch = useAppDispatch();
  const t2 = entityType === "members" ? ChangeRoleEntityType.member : ChangeRoleEntityType.team;
  let t3;
  if ($[2] !== newRole || $[3] !== t2) {
    t3 = {
      subjectType: "workspace",
      newRole,
      entityType: t2
    };
    $[2] = newRole;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    bulkDialogContent,
    successDialogContent
  } = useChangeRoleBulkDialogContent(t3);
  const t4 = entityType === "members";
  let t5;
  if ($[5] !== newRole || $[6] !== selectedEntities || $[7] !== t4) {
    t5 = {
      doesRoleNeedXgSeat: doesWorkspaceRoleRequireXgSeat,
      newRole,
      selectedEntities,
      isEnabled: t4
    };
    $[5] = newRole;
    $[6] = selectedEntities;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    getSkipReason,
    isRoleDisabled,
    xgSeatAlert,
    shouldDisableConfirm,
    changeRoleWithXgSeat,
    refreshSeatUsage
  } = useBulkRoleXgSeats(t5);
  const assignableRoles = getBulkAssignableCompanyRoles(entityType, canAssignManagers, canPlanRequireXgSeat);
  let t6;
  let t7;
  if ($[9] !== shouldShowSuccessDialog) {
    t6 = () => {
      if (shouldShowSuccessDialog) {
        setShouldShowDialog(false);
      }
    };
    t7 = [shouldShowSuccessDialog];
    $[9] = shouldShowSuccessDialog;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== currentUser || $[13] !== dispatch || $[14] !== getSkipReason || $[15] !== selectedEntities) {
    t8 = function onNewRoleClicked(event) {
      dispatch(setBulkActionName("changeRole"));
      const role = event.currentTarget.dataset.myValue;
      const notAllowedChanges = [];
      for (const entity of selectedEntities) {
        if (role === "MEMBER" && (entity.role === "COMPANY_MANAGER" || entity.role === "PROJECT_MANAGER")) {
          notAllowedChanges.push({
            id: entity.identity,
            changes: {
              status: "not-allowed",
              message: `You cannot change from ${formatUserRoleType(entity.role)} role to ${formatUserRoleType("MEMBER")}.`
            }
          });
          continue;
        }
        const xgSeatSkipReason = getSkipReason(entity, role);
        if (xgSeatSkipReason) {
          notAllowedChanges.push({
            id: entity.identity,
            changes: {
              status: "not-allowed",
              message: xgSeatSkipReason
            }
          });
        }
      }
      if (notAllowedChanges.length) {
        dispatch(updateMany(notAllowedChanges));
      }
      if (currentUser) {
        dispatch(updateOne({
          id: currentUser.identity,
          changes: {
            status: "not-allowed",
            message: "You cannot change the role of yourself."
          }
        }));
      }
      setNewRole(role);
      setShouldShowDialog(true);
    };
    $[12] = currentUser;
    $[13] = dispatch;
    $[14] = getSkipReason;
    $[15] = selectedEntities;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const onNewRoleClicked = t8;
  let t9;
  if ($[17] !== mode) {
    t9 = getBulkActionButtonConfig(mode, "Change role in workspace");
    $[17] = mode;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const config = t9;
  const T0 = FaroTextMenuButton;
  let t10;
  if ($[19] !== config.shouldShowText) {
    t10 = config.shouldShowText ? <Text variant="buttonM">Change role</Text> : undefined;
    $[19] = config.shouldShowText;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = config.tooltipText;
  let t12;
  if ($[21] !== config.sx) {
    t12 = () => config.sx;
    $[21] = config.sx;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      anchorOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "bottom",
        horizontal: "left"
      }
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = config.rippleColor;
  const t15 = false;
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <UserRoleIcon sx={{
      width: "18px",
      height: "18px"
    }} />;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== isRoleDisabled || $[26] !== onNewRoleClicked) {
    t17 = role_0 => <MenuItem data-my-value={role_0} key={role_0} value={role_0} onClick={onNewRoleClicked} disabled={isRoleDisabled(role_0)} sx={getRoleMenuItemSx({
      isTableCell: false
    })}><RoleMenuItem role={role_0} /></MenuItem>;
    $[25] = isRoleDisabled;
    $[26] = onNewRoleClicked;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const t18 = assignableRoles.map(t17);
  let t19;
  if ($[28] !== T0 || $[29] !== config.rippleColor || $[30] !== config.tooltipText || $[31] !== t10 || $[32] !== t12 || $[33] !== t13 || $[34] !== t16 || $[35] !== t18) {
    t19 = <T0 buttonTitle={t10} tooltipText={t11} buttonSx={t12} menuPosition={t13} rippleColor={t14} shouldShowEndIcon={t15} startIcon={t16}>{t18}</T0>;
    $[28] = T0;
    $[29] = config.rippleColor;
    $[30] = config.tooltipText;
    $[31] = t10;
    $[32] = t12;
    $[33] = t13;
    $[34] = t16;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== bulkActionName || $[38] !== shouldShowSuccessDialog || $[39] !== successDialogContent) {
    t20 = bulkActionName === "changeRole" && shouldShowSuccessDialog && <BulkSuccessDialog dialogContent={successDialogContent} />;
    $[37] = bulkActionName;
    $[38] = shouldShowSuccessDialog;
    $[39] = successDialogContent;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== bulkActionName || $[42] !== bulkDialogContent || $[43] !== changeRoleWithXgSeat || $[44] !== companyId || $[45] !== dispatch || $[46] !== entityType || $[47] !== newRole || $[48] !== refreshSeatUsage || $[49] !== selectedEntities || $[50] !== shouldDisableConfirm || $[51] !== shouldShowDialog || $[52] !== xgSeatAlert) {
    t21 = bulkActionName === "changeRole" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={() => {
      setShouldShowDialog(false);
      refreshSeatUsage();
    }} trackingEvent={BulkActionEvents.bulkChangeRole} shouldDisableConfirmButton={shouldDisableConfirm} bulkActionCallback={member => changeRoleWithXgSeat(member, () => dispatch(updateMemberRoleInWorkspace({
      companyId,
      role: newRole,
      identity: member.identity
    })).unwrap())} onSuccessfulComplete={refreshSeatUsage} selectedEntities={selectedEntities} uniqueIdKey="identity" tableDataType={entityType}>{xgSeatAlert}</FaroBulkDialog>;
    $[41] = bulkActionName;
    $[42] = bulkDialogContent;
    $[43] = changeRoleWithXgSeat;
    $[44] = companyId;
    $[45] = dispatch;
    $[46] = entityType;
    $[47] = newRole;
    $[48] = refreshSeatUsage;
    $[49] = selectedEntities;
    $[50] = shouldDisableConfirm;
    $[51] = shouldShowDialog;
    $[52] = xgSeatAlert;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t19 || $[55] !== t20 || $[56] !== t21) {
    t22 = <>{t19}{t20}{t21}</>;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  return t22;
}